import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "@/plugins/firebase";

const ADMIN_LEVELS = {
  "Question Writer": 1,
  Admin: 2,
  "Super Admin": 3,
};
export const ADMIN_ACCESS_CACHE_TTL_MS = 10 * 60 * 1000;

export const useAdminAccessStore = defineStore("adminAccess", () => {
  const status = ref("idle");
  const level = ref(null);
  const bankAccess = ref([]);
  const manageVideos = ref(false);
  const resolvedForUid = ref(null);
  const lastFetchedAt = ref(null);
  const loadError = ref(null);
  let inFlightPromise = null;
  let inFlightUid = null;
  let requestVersion = 0;

  const isAdmin = computed(() => level.value !== null);
  const isSuperAdmin = computed(() => level.value === "Super Admin");
  const canManageVideos = computed(
    () =>
      manageVideos.value ||
      isSuperAdmin.value ||
      level.value === "Video Manager",
  );
  const canModerate = computed(() =>
    ["Admin", "Super Admin"].includes(level.value),
  );

  const clear = () => {
    requestVersion += 1;
    status.value = "idle";
    level.value = null;
    bankAccess.value = [];
    manageVideos.value = false;
    resolvedForUid.value = null;
    lastFetchedAt.value = null;
    loadError.value = null;
    inFlightPromise = null;
    inFlightUid = null;
  };

  const isCacheFresh = (currentUid, maxAgeMs) => {
    if (!currentUid || resolvedForUid.value !== currentUid) {
      return false;
    }

    if (status.value !== "loaded" || !lastFetchedAt.value) {
      return false;
    }

    return Date.now() - lastFetchedAt.value < maxAgeMs;
  };

  const getSnapshot = () => ({
    isAdmin: isAdmin.value,
    level: level.value,
    bankAccess: [...bankAccess.value],
    manageVideos: canManageVideos.value,
  });

  const getEmptySnapshot = () => ({
    isAdmin: false,
    level: null,
    bankAccess: [],
    manageVideos: false,
  });

  const hasRequiredLevel = (requiredLevel) => {
    if (!requiredLevel) return isAdmin.value;

    const userLevelValue = ADMIN_LEVELS[level.value] || 0;
    const requiredLevels = Array.isArray(requiredLevel)
      ? requiredLevel
      : [requiredLevel];

    return requiredLevels.some((candidate) => {
      const requiredLevelValue = ADMIN_LEVELS[candidate] || 0;
      return userLevelValue >= requiredLevelValue;
    });
  };

  const hasExactLevel = (requiredLevel) => {
    if (!requiredLevel) return isAdmin.value;

    const requiredLevels = Array.isArray(requiredLevel)
      ? requiredLevel
      : [requiredLevel];

    return requiredLevels.includes(level.value);
  };

  const canAccessBank = (bank) => {
    if (!bank) return isAdmin.value;
    return bankAccess.value.includes(bank);
  };

  const canAccessAnyBank = (banks = []) => {
    if (!Array.isArray(banks) || banks.length === 0) {
      return isAdmin.value;
    }
    return banks.some((bank) => canAccessBank(bank));
  };

  const hasAnyAccess = (requirements = []) => {
    if (!Array.isArray(requirements) || requirements.length === 0) {
      return isAdmin.value;
    }

    return requirements.some((requirement) => {
      if (!requirement || typeof requirement !== "object") return false;
      if (requirement.level && hasRequiredLevel(requirement.level)) return true;
      if (requirement.exactLevel && hasExactLevel(requirement.exactLevel)) {
        return true;
      }
      if (requirement.manageVideos && canManageVideos.value) return true;
      if (requirement.bank && canAccessBank(requirement.bank)) return true;
      if (Array.isArray(requirement.anyBank)) {
        return canAccessAnyBank(requirement.anyBank);
      }
      return false;
    });
  };

  const fetchAccess = async (currentUid, requestId) => {
    status.value = "loading";
    loadError.value = null;

    try {
      const callable = httpsCallable(functions, "adminGetMyAccess");
      const result = await callable();
      const data = result?.data || {};

      if (
        requestId !== requestVersion ||
        auth.currentUser?.uid !== currentUid
      ) {
        return getSnapshot();
      }

      level.value = data.isAdmin ? data.level || null : null;
      bankAccess.value = Array.isArray(data.bankAccess) ? data.bankAccess : [];
      manageVideos.value = data.manageVideos === true;
      resolvedForUid.value = currentUid;
      lastFetchedAt.value = Date.now();
      status.value = "loaded";

      return getSnapshot();
    } catch (error) {
      if (
        requestId !== requestVersion ||
        auth.currentUser?.uid !== currentUid
      ) {
        return getSnapshot();
      }

      clear();
      loadError.value = error;
      status.value = "error";
      throw error;
    }
  };

  const loadAccess = (
    user = auth.currentUser,
    { force = false, maxAgeMs = ADMIN_ACCESS_CACHE_TTL_MS } = {},
  ) => {
    const currentUid = user?.uid || null;

    if (!currentUid) {
      clear();
      return Promise.resolve(getEmptySnapshot());
    }

    if (resolvedForUid.value && resolvedForUid.value !== currentUid) {
      clear();
    }

    if (inFlightUid && inFlightUid !== currentUid) {
      clear();
    }

    if (!force && isCacheFresh(currentUid, maxAgeMs)) {
      return Promise.resolve(getSnapshot());
    }

    if (inFlightPromise && inFlightUid === currentUid) {
      return inFlightPromise;
    }

    const requestId = ++requestVersion;
    inFlightUid = currentUid;
    inFlightPromise = fetchAccess(currentUid, requestId).finally(() => {
      if (requestId === requestVersion) {
        inFlightPromise = null;
        inFlightUid = null;
      }
    });

    return inFlightPromise;
  };

  const ensureLoaded = (user = auth.currentUser, options = {}) =>
    loadAccess(user, options);

  const refresh = (user = auth.currentUser) =>
    loadAccess(user, { force: true });

  return {
    status,
    level,
    bankAccess,
    manageVideos,
    resolvedForUid,
    lastFetchedAt,
    loadError,
    isAdmin,
    isSuperAdmin,
    canManageVideos,
    canModerate,
    clear,
    hasRequiredLevel,
    hasExactLevel,
    hasAnyAccess,
    canAccessBank,
    canAccessAnyBank,
    ensureLoaded,
    refresh,
  };
});
